/**
 * One-entry per-session cache for server-authorized directory calls (the
 * thread-id list, the community roster). Exists because both endpoints are
 * rate-limited per account and their client hooks refetched on every mount,
 * so ordinary navigation spent the whole budget: Bobbie hit "Messages
 * refreshed a lot. Wait a moment, then try again." reading her own inbox
 * (2026-08-13). The lists rarely change; serving a recent copy makes
 * navigation free and saves the real requests for the moments that need one.
 *
 * Semantics, all of them load-bearing:
 * - Entries are keyed by uid. A different uid never reads another account's
 *   entry, and a finished fetch lands in the cache only while its uid is
 *   still the one in play, so an account switch on a shared device cannot
 *   resurface the previous account's data.
 * - Concurrent loads for one uid share a single in-flight request, forceFresh
 *   callers included. Honest bound, not a free lunch: a joined response can
 *   be a snapshot computed just before the event that motivated the
 *   forceFresh, since it was already on the wire. Consumers absorb that
 *   sub-second window: useThread's recovery spends its one pass on it and a
 *   reopened screen starts a genuinely fresh request, so the worst case is
 *   one visible retry, never a wrong grant.
 * - forceFresh otherwise bypasses a fresh entry. Callers reach for it when
 *   the cached answer just proved wrong (a subscription the list vouched for
 *   was refused) or when the answer must be authoritative (declaring a
 *   conversation gone for good).
 * - A failed request caches nothing and rejects every caller that joined it,
 *   so an error is never quietly replayed as data and never poisons the next
 *   attempt.
 */

export interface DirectoryCacheLoad<T> {
  value: T;
  /** 'cache' when a fresh session entry answered without a network request. */
  served: 'cache' | 'network';
}

export interface DirectoryCache<T> {
  load(
    uid: string,
    fetcher: () => Promise<T>,
    options?: { forceFresh?: boolean; now?: () => number }
  ): Promise<DirectoryCacheLoad<T>>;
}

interface DirectoryCacheEntry<T> {
  uid: string;
  value: T;
  at: number;
}

interface DirectoryCacheRequest<T> {
  uid: string;
  promise: Promise<T>;
}

export function createDirectoryCache<T>(freshMs: number): DirectoryCache<T> {
  let entry: DirectoryCacheEntry<T> | null = null;
  let inFlight: DirectoryCacheRequest<T> | null = null;

  return {
    async load(uid, fetcher, options = {}) {
      const now = options.now ?? Date.now;
      if (
        !options.forceFresh &&
        entry &&
        entry.uid === uid &&
        now() - entry.at < freshMs
      ) {
        return { value: entry.value, served: 'cache' };
      }
      if (inFlight && inFlight.uid === uid) {
        return { value: await inFlight.promise, served: 'network' };
      }
      const request: DirectoryCacheRequest<T> = { uid, promise: fetcher() };
      inFlight = request;
      try {
        const value = await request.promise;
        // Guarded so a slow response finishing after an account switch (or
        // after a newer request replaced this one) cannot overwrite the
        // entry that now belongs to someone else.
        if (inFlight === request) {
          entry = { uid, value, at: now() };
        }
        return { value, served: 'network' };
      } finally {
        if (inFlight === request) inFlight = null;
      }
    },
  };
}
